/**
 * Loads one case's static evidence bundle and shares it through context for
 * everything mounted under this provider. Completed views render entirely
 * from this immutable, already-validated data (architecture section 12's
 * "UI state" note) -- no component re-fetches or re-validates it. Re-fetches
 * only when `baseUrl` itself changes (switching cases), driven by the
 * `key`-remounted `BundleProvider` in `App.tsx`.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

import type { ErrorDetail, EvidenceBundleView } from "@exploitlens/ui-contracts";

import { loadEvidenceBundle } from "../lib/load-bundle";

export type BundleState =
  | { status: "loading" }
  | { status: "error"; error: ErrorDetail }
  | { status: "ready"; bundle: EvidenceBundleView };

const BundleContext = createContext<BundleState | undefined>(undefined);

export function BundleProvider({ baseUrl, children }: { baseUrl?: string; children: ReactNode }) {
  const [state, setState] = useState<BundleState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    loadEvidenceBundle(baseUrl !== undefined ? { baseUrl } : {})
      .then((result) => {
        if (cancelled) return;
        setState(
          result.ok
            ? { status: "ready", bundle: result.value }
            : { status: "error", error: result.error },
        );
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setState({
          status: "error",
          error: {
            code: "INTERNAL",
            message: cause instanceof Error ? cause.message : String(cause),
          },
        });
      });
    return () => {
      cancelled = true;
    };
  }, [baseUrl]);

  return <BundleContext.Provider value={state}>{children}</BundleContext.Provider>;
}

export function useBundleState(): BundleState {
  const state = useContext(BundleContext);
  if (state === undefined) {
    throw new Error("useBundleState must be used within a BundleProvider");
  }
  return state;
}

/** Convenience for routes that can only render once the bundle is ready (they are only mounted then). */
export function useBundle(): EvidenceBundleView {
  const state = useBundleState();
  if (state.status !== "ready") {
    throw new Error("useBundle called before the evidence bundle finished loading");
  }
  return state.bundle;
}
